import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const data = await api.getCurrentUser();
      if (data && data.user) {
        setUser(data.user);
        setOnboardingComplete(data.onboarding_complete);
      } else {
        setUser(null);
        setOnboardingComplete(false);
      }
    } catch (error) {
      setUser(null);
      setOnboardingComplete(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await api.login({ username, password });
    // Backend may return user data, or we need to fetch it
    if (response && response.user) {
      setUser(response.user);
      setOnboardingComplete(response.onboarding_complete || false);
    } else {
      // If response doesn't have user, fetch it
      await checkAuth();
    }
  };

  const register = async (username, email, password) => {
    const response = await api.register({ username, email, password });
    // Backend may return user data, or we need to fetch it
    if (response && response.user) {
      setUser(response.user);
      setOnboardingComplete(response.onboarding_complete || false);
    } else {
      // If response doesn't have user, fetch it
      await checkAuth();
    }
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setOnboardingComplete(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        onboardingComplete,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
